export const clamp = value => Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
const smooth = value => { const n = clamp(value); return n * n * (3 - 2 * n); };

export function categoryKeyIndex(key, index, count) {
  if (!Number.isInteger(count) || count < 1 || !Number.isInteger(index) || index < 0 || index >= count) return null;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  if (key === 'ArrowDown' || key === 'ArrowRight') return (index + 1) % count;
  if (key === 'ArrowUp' || key === 'ArrowLeft') return (index - 1 + count) % count;
  return null;
}

export function nextStoryIndex(current, count, step = 1) {
  if (!Number.isInteger(count) || count < 1 || !Number.isInteger(current) || !Number.isInteger(step)) return 0;
  return ((current + step) % count + count) % count;
}

export function storyKeyIndex(key, current, count) {
  if (!Number.isInteger(count) || count < 1) return null;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  if (key === 'ArrowLeft') return Math.max(0, current - 1);
  if (key === 'ArrowRight') return Math.min(count - 1, current + 1);
  return null;
}

export function journalRotationAllowed({ count, paused, visible, pageVisible, staticMode }) {
  return count > 1 && !paused && visible === true && pageVisible === true && !staticMode;
}

export function paintingMotion({ width = 0, height = 0, progress = 0, still = false } = {}) {
  width = Number.isFinite(width) ? Math.max(0, width) : 0;
  height = Number.isFinite(height) ? Math.max(0, height) : 0;
  const p = still ? 0 : clamp(progress);
  const narrow = width <= 700;
  const compact = !narrow && width <= 1000;
  const x = width * (narrow ? .5 : compact ? .35 : .39);
  const y = height * (narrow ? .60 : .51);
  const initial = narrow ? width * .46 : compact ? Math.min(width * .29, height * .42) : Math.min(width * .31, height * .455);
  const end = Math.hypot(Math.max(x, width - x), Math.max(y, height - y)) + 100;
  const t = clamp(p / .86);
  const travel = t * .4 + smooth(t) * .6;
  const radius = initial > 0 ? initial * Math.pow(end / initial, travel) : 0;
  return { entered: still || p > .7, vars: {
    '--progress': p, '--gate-x': `${x}px`, '--gate-y': `${y}px`, '--gate-r': `${radius}px`, '--gate-thickness': '3px',
    '--front-opacity': 1 - smooth(p / .35), '--room-copy': smooth((p - .48) / .22), '--screen-shift': `${(1 - p) * 44}%`, '--photo-drift': `${narrow ? (1 - p) * height * .22 : 0}px`,
  } };
}

export function teaMotion({ width = 0, progress = 0, still = false } = {}) {
  width = Number.isFinite(width) ? Math.max(0, width) : 0;
  const p = still ? 1 : clamp(progress);
  const narrow = width <= 700;
  const apertureWidth = (narrow ? .84 : .68) + p * (narrow ? .28 : .44);
  const apertureHeight = .82 + p * .3;
  return { open: still || p > .62, vars: {
    '--door-x': `${(1 - apertureWidth) * 50}%`, '--door-y': `${(1 - apertureHeight) * 50}%`, '--door-width': `${apertureWidth * 100}%`, '--door-height': `${apertureHeight * 100}%`,
    '--tea-progress': p, '--tea-photo-shift': 1 - Math.min(1, p / .4), '--tea-copy': clamp((p - .4) / .22), '--door-caption': p > .18 ? '#e0e0d8' : '#55554e',
  } };
}

export function startArtSite(doc = document, { loadGate: loadGateModule } = {}) {
  const win = doc.defaultView;
  const lifetime = new AbortController();
  const signal = lifetime.signal;
  const instances = new Map();
  const scenes = new Set();
  const updates = new Set();
  const journals = new Set();
  const media = {
    reduced: win.matchMedia('(prefers-reduced-motion: reduce)'), contrast: win.matchMedia('(forced-colors: active)'),
    print: win.matchMedia('print'), short: win.matchMedia('(max-height: 640px)'),
  };
  let disposed = false, frame = 0, gate = null, gateRequest = null, printing = false, pageActive = true;
  const motionStill = () => printing || media.reduced.matches || media.contrast.matches || media.print.matches;
  const homeStill = () => motionStill() || media.short.matches || !win.ResizeObserver;
  const resize = win.ResizeObserver ? new win.ResizeObserver(() => schedule()) : null;
  const setVars = (node, values) => Object.entries(values).forEach(([key, value]) => node.style.setProperty(key, value));
  const on = (node, type, listener, options = {}) => node?.addEventListener(type, listener, { ...options, signal });
  const all = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const focusHeading = node => {
    const heading = node?.querySelector('h2') || node;
    if (!heading) return;
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  };

  function measurePassages() {
    if (!gate) return;
    const header = doc.querySelector('.site-header');
    const viewportHeight = win.innerHeight;
    const headerOffset = header ? Math.min(viewportHeight, Math.max(0, header.getBoundingClientRect().bottom)) : 0;
    const readings = [];
    for (const scene of scenes) {
      if (!scene.isConnected || scene.closest('[hidden]')) continue;
      const anchor = scene.querySelector('.passage-photo-anchor');
      if (!anchor) continue;
      const sceneBox = scene.getBoundingClientRect();
      const extension = scene.querySelector('.home-category-architecture');
      const category = extension && scene.closest('.home-category-explorer');
      const rule = category?.querySelector('.collection-rule');
      const wide = category && (scene.closest('[data-art-canvas]')?.clientWidth || win.innerWidth) > 700;
      const headroom = wide && rule ? Math.max(0, sceneBox.top - rule.getBoundingClientRect().bottom - 18) : 0;
      const box = { width: sceneBox.width, height: sceneBox.height + headroom, left: sceneBox.left, top: sceneBox.top - headroom };
      const photo = anchor.getBoundingClientRect();
      if (box.width <= 0 || box.height <= 0 || photo.width <= 0 || photo.height <= 0) continue;
      const reveal = wide && gate.categoryGate ? gate.categoryGate : gate.verticalGate;
      const geometry = reveal({ width: box.width, height: box.height, photo: { x: photo.left - box.left, y: photo.top - box.top, width: photo.width, height: photo.height }, photoViewportTop: photo.top, viewportHeight, headerOffset, variant: scene.dataset.directoryPassage, narrow: box.width <= 700, still: motionStill() || Boolean(wide && media.short.matches), engaged: Boolean(category?.classList.contains('is-engaged')) });
      const pier = scene.querySelector('.scene-pier-plane');
      const rear = gate.projectRearLayers({ height: box.height, pierTop: Number(pier?.dataset.pierTop), pierBottom: Number(pier?.dataset.pierBottom), eaveY: geometry.eaveY, groundY: geometry.groundY });
      readings.push({ scene, box, geometry, rear, headroom });
    }
    for (const { scene, box, geometry, rear, headroom } of readings) {
      const svg = scene.querySelector('.passage-front');
      if (!svg) continue;
      svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
      for (const [selector, path] of [['.passage-wall', geometry.wallPath], ['.passage-rim', geometry.rimPath], ['.passage-reveal', geometry.revealPath], ['.passage-joinery', geometry.ornamentPath]]) svg.querySelector(selector)?.setAttribute('d', path);
      const clip = geometry.wallPath ? `path(evenodd, "${geometry.wallPath}")` : 'none';
      for (const selector of ['.passage-distance-clip', '.passage-rear', '.passage-near-clip']) {
        const layer = scene.querySelector(selector);
        if (layer) layer.style.clipPath = selector === '.passage-near-clip' && geometry.nearWallPath
          ? `path(evenodd, "${geometry.nearWallPath}")` : clip;
      }
      setVars(scene, {
        '--category-scene-headroom': `${headroom}px`,
        '--category-reveal-progress': geometry.progress ?? geometry.opening,
        '--passage-near-x': `${geometry.nearX}px`, '--passage-near-y': `${geometry.nearY}px`, '--passage-distance-x': `${geometry.distanceX}px`, '--passage-distance-y': `${geometry.distanceY}px`,
        '--passage-eave-y': `${rear.eaveY}px`, '--passage-ground-y': `${rear.groundY}px`, '--passage-pier-scale': rear.pierScale, '--passage-near-scale': geometry.nearScale,
        '--passage-near-ink': geometry.nearInk, '--passage-far-ink': geometry.farInk, '--passage-reveal-alpha': geometry.revealAlpha,
        '--passage-flow-offset': geometry.flowOffset, '--passage-ground-offset': geometry.groundOffset, '--passage-flow-alpha': geometry.flowAlpha, '--passage-ground-alpha': geometry.groundAlpha,
      });
    }
  }

  function measure() {
    frame = 0;
    if (disposed) return;
    for (const canvas of doc.querySelectorAll('[data-art-canvas], .canvas')) canvas.classList.toggle('reduced', homeStill());
    for (const node of doc.querySelectorAll('.collection-walk, .home-category-explorer')) node.classList.toggle('is-still', motionStill() || !gate || (node.matches('.home-category-explorer') && media.short.matches));
    updates.forEach(update => update());
    measurePassages();
  }

  function schedule() {
    if (!frame && !disposed) frame = win.requestAnimationFrame(measure);
  }

  function register(node, destroy) { instances.set(node, destroy); }
  function listen(node, type, listener, scope, options = {}) { node.addEventListener(type, listener, { ...options, signal: scope.signal }); }

  function mountPainting(node) {
    const stage = node.querySelector('.stage'), copy = node.querySelector('.painting-copy');
    if (!stage) return;
    const scope = new AbortController();
    const button = node.querySelector('[data-paintings-enter]');
    let entered = true, focusOnEntry = false;
    const update = () => {
      const offset = parseFloat(win.getComputedStyle(stage).top) || 0;
      const progress = (offset - node.getBoundingClientRect().top) / Math.max(1, node.offsetHeight - stage.offsetHeight);
      const state = paintingMotion({ width: stage.clientWidth, height: stage.clientHeight, progress, still: homeStill() });
      entered = state.entered;
      setVars(stage, state.vars);
      stage.classList.toggle('entered', entered);
      if (copy) copy.inert = !entered;
      const label = button?.querySelector('[data-paintings-label]');
      if (label) label.textContent = entered ? button.dataset.nextLabel : button.dataset.enterLabel;
      const caption = node.querySelector('[data-paintings-caption]');
      if (caption) caption.textContent = entered ? caption.dataset.nextCaption : caption.dataset.entryCaption;
      if (entered && focusOnEntry) { focusOnEntry = false; focusHeading(copy); }
    };
    if (button) listen(button, 'click', () => {
      if (entered) {
        const next = doc.querySelector('[data-art-teaware]');
        next?.scrollIntoView({ block: 'start', behavior: homeStill() ? 'auto' : 'smooth' });
        if (next) { next.setAttribute('tabindex', '-1'); next.focus({ preventScroll: true }); }
      } else {
        focusOnEntry = true;
        const offset = parseFloat(win.getComputedStyle(stage).top) || 0;
        const start = win.scrollY + node.getBoundingClientRect().top - offset;
        win.scrollTo({ top: start + .8 * (node.offsetHeight - stage.offsetHeight), behavior: homeStill() ? 'auto' : 'smooth' });
        schedule();
      }
    }, scope);
    updates.add(update); resize?.observe(node); resize?.observe(stage);
    register(node, () => { scope.abort(); updates.delete(update); resize?.unobserve(node); resize?.unobserve(stage); if (copy) copy.inert = false; });
  }

  function mountTea(node) {
    const stage = node.querySelector('.tea-stage'), copy = node.querySelector('.table-copy'), caption = node.querySelector('.tea-caption');
    if (!stage) return;
    const scope = new AbortController();
    const button = node.querySelector('[data-tea-enter]');
    let focusOnEntry = false;
    const update = () => {
      const offset = parseFloat(win.getComputedStyle(stage).top) || 0;
      const progress = (offset - node.getBoundingClientRect().top) / Math.max(1, node.offsetHeight - stage.offsetHeight);
      const state = teaMotion({ width: stage.clientWidth, progress, still: homeStill() });
      setVars(stage, state.vars);
      stage.classList.toggle('is-open', state.open);
      if (copy) copy.inert = !state.open;
      if (caption) caption.inert = state.open;
      if (state.open && focusOnEntry) { focusOnEntry = false; focusHeading(copy); }
    };
    if (button) listen(button, 'click', () => {
      focusOnEntry = true;
      const offset = parseFloat(win.getComputedStyle(stage).top) || 0;
      const start = win.scrollY + node.getBoundingClientRect().top - offset;
      win.scrollTo({ top: start + .85 * (node.offsetHeight - stage.offsetHeight), behavior: homeStill() ? 'auto' : 'smooth' });
      schedule();
    }, scope);
    updates.add(update); resize?.observe(node); resize?.observe(stage);
    register(node, () => { scope.abort(); updates.delete(update); resize?.unobserve(node); resize?.unobserve(stage); if (copy) copy.inert = false; if (caption) caption.inert = false; });
  }

  function mountCategories(node) {
    const scope = new AbortController();
    const buttons = [...node.querySelectorAll('[data-category-select]')];
    const panels = [...node.querySelectorAll('[data-category-panel]')];
    const arrows = new Map(buttons.map(button => [button, button.querySelector('.category-mark')?.firstElementChild?.cloneNode(true)]));
    const mark = (button, active) => {
      const target = button.querySelector('.category-mark');
      if (!target) return;
      if (!active) { const arrow = arrows.get(button); if (arrow) target.replaceChildren(arrow.cloneNode(true)); return; }
      const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
      for (const [name, value] of Object.entries({ class: 'detail-lattice', viewBox: '0 0 24 24', 'aria-hidden': 'true', focusable: 'false' })) svg.setAttribute(name, value);
      for (const [path, css] of [['M3 3h18v18H3ZM3 8h5V3m8 0v5h5m0 8h-5v5M8 21v-5H3', ''], ['M8 8h8v8H8Z', 'detail-lattice-center']]) {
        const shape = doc.createElementNS('http://www.w3.org/2000/svg', 'path'); shape.setAttribute('d', path); if (css) shape.setAttribute('class', css); svg.append(shape);
      }
      target.replaceChildren(svg);
    };
    buttons.forEach(button => mark(button, button.dataset.categoryActive === 'true'));
    const select = (id, { engage = false } = {}) => {
      if (!panels.some(panel => panel.dataset.categoryPanel === id)) return;
      if (engage) node.classList.add('is-engaged');
      buttons.forEach(button => { const active = button.dataset.categorySelect === id; button.setAttribute('data-category-active', String(active)); if (button.tagName === 'BUTTON') button.setAttribute('aria-pressed', String(active)); mark(button, active); });
      panels.forEach(panel => { panel.hidden = panel.dataset.categoryPanel !== id; });
      schedule();
    };
    for (const [index, button] of buttons.entries()) {
      listen(button, 'pointerenter', event => {
        if (event.pointerType !== 'mouse' || panels.some(panel => panel.contains(doc.activeElement)) || node.querySelector('[data-category-select]:focus-visible')) return;
        select(button.dataset.categorySelect);
      }, scope);
      listen(button, 'click', () => select(button.dataset.categorySelect, { engage: true }), scope);
      listen(button, 'focus', () => select(button.dataset.categorySelect, { engage: true }), scope);
      listen(button, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const indexToFocus = categoryKeyIndex(event.key, index, buttons.length);
        if (indexToFocus === null) return;
        event.preventDefault(); select(buttons[indexToFocus].dataset.categorySelect, { engage: true }); buttons[indexToFocus].focus();
      }, scope);
    }
    listen(node, 'shopify:block:select', event => {
      const panel = event.target.closest('[data-category-panel]');
      if (panel && node.contains(panel)) select(panel.dataset.categoryPanel, { engage: true });
    }, scope);
    register(node, () => scope.abort());
  }

  function mountJournal(node) {
    const scope = new AbortController();
    const leaves = [...node.querySelectorAll('[data-journal-story]')];
    if (!leaves.length) return;
    const stage = node.querySelector('.home-journal-stage');
    const previous = node.querySelector('[data-journal-prev]'), next = node.querySelector('[data-journal-next]'), rotation = node.querySelector('[data-journal-rotation]');
    const position = node.querySelector('[data-journal-position]'), count = node.querySelector('[data-journal-count]'), announcement = node.querySelector('[data-journal-announcement]');
    const initialMessage = announcement?.textContent || '';
    const template = initialMessage.replace(/\b1\b/, '{{current}}');
    const interval = Math.max(4000, Math.min(10000, Number(node.dataset.interval) || 4500));
    let current = Math.max(0, leaves.findIndex(leaf => leaf.classList.contains('is-current'))), paused = node.dataset.autoplay !== 'true', visible = false, timer = 0, gone = false;
    const clear = () => { win.clearTimeout(timer); timer = 0; };
    const sync = () => {
      clear();
      if (gone) return;
      const still = motionStill();
      node.classList.toggle('is-still', still);
      leaves.forEach((leaf, index) => { const active = index === current; leaf.classList.toggle('is-current', active); leaf.inert = !active; leaf.setAttribute('aria-hidden', String(!active)); });
      if (count) count.textContent = `${String(current + 1).padStart(2, '0')} / ${String(leaves.length).padStart(2, '0')}`;
      if (announcement) announcement.textContent = `${template.replace('{{current}}', String(current + 1))}: ${leaves[current].querySelector('h2')?.textContent || ''}`;
      position?.setAttribute('aria-live', paused || still ? 'polite' : 'off');
      if (rotation) {
        rotation.disabled = still || leaves.length < 2;
        rotation.setAttribute('aria-label', paused || still ? rotation.dataset.playLabel : rotation.dataset.pauseLabel);
        const play = rotation.querySelector('[data-journal-play-icon]'), pause = rotation.querySelector('[data-journal-pause-icon]');
        if (play) play.style.display = paused || still ? '' : 'none';
        if (play) play.removeAttribute('hidden');
        if (pause) pause.style.display = paused || still ? 'none' : '';
      }
      if (journalRotationAllowed({ count: leaves.length, paused, visible, pageVisible: pageActive && doc.visibilityState === 'visible', staticMode: still })) timer = win.setTimeout(() => { current = nextStoryIndex(current, leaves.length); sync(); }, interval);
    };
    const select = index => { current = index; sync(); };
    if (previous) listen(previous, 'click', () => select(nextStoryIndex(current, leaves.length, -1)), scope);
    if (next) listen(next, 'click', () => select(nextStoryIndex(current, leaves.length)), scope);
    if (rotation) listen(rotation, 'click', () => { paused = !paused; sync(); }, scope);
    if (stage) listen(stage, 'keydown', event => {
      if (event.target !== stage || event.altKey || event.ctrlKey || event.metaKey) return;
      const index = storyKeyIndex(event.key, current, leaves.length);
      if (index === null) return;
      event.preventDefault(); paused = true; select(index);
    }, scope);
    listen(node, 'focusin', event => {
      if (event.target !== node && event.target.matches(':focus-visible') && !event.target.closest('[data-journal-rotation]')) { paused = true; sync(); }
    }, scope);
    listen(node, 'shopify:block:select', event => {
      const leaf = event.target.closest('[data-journal-story]');
      const index = leaves.indexOf(leaf);
      if (index !== -1) { paused = true; select(index); }
    }, scope);
    const observer = win.IntersectionObserver ? new win.IntersectionObserver(entries => { visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .35; sync(); }, { threshold: [0, .35] }) : null;
    observer?.observe(node); journals.add(sync); sync();
    register(node, () => { gone = true; clear(); scope.abort(); observer?.disconnect(); journals.delete(sync); });
  }

  function mountMenu(header) {
    const trigger = header.querySelector('[data-art-menu-toggle]'), menu = header.querySelector('[data-art-menu]');
    if (!trigger || !menu) return;
    const scope = new AbortController();
    const setOpen = open => { trigger.setAttribute('aria-expanded', String(open)); menu.hidden = !open; trigger.querySelector('.menu-indicator')?.classList.toggle('is-open', open); };
    const closeOutside = event => { if (trigger.getAttribute('aria-expanded') === 'true' && event.target instanceof win.Node && !header.contains(event.target)) setOpen(false); };
    listen(trigger, 'click', () => setOpen(trigger.getAttribute('aria-expanded') !== 'true'), scope);
    listen(doc, 'keydown', event => { if (event.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') { event.preventDefault(); setOpen(false); trigger.focus({ preventScroll: true }); } }, scope);
    listen(doc, 'pointerdown', closeOutside, scope);
    listen(doc, 'focusin', closeOutside, scope);
    listen(menu, 'click', event => { if (event.target.closest('a')) setOpen(false); }, scope);
    setOpen(false); resize?.observe(header);
    register(header, () => { scope.abort(); resize?.unobserve(header); setOpen(false); });
  }

  function mount(root) {
    for (const [selector, setup] of [['[data-art-paintings]', mountPainting], ['[data-art-teaware]', mountTea], ['[data-category-explorer]', mountCategories], ['[data-art-journal]', mountJournal], ['.site-header', mountMenu]]) {
      for (const node of all(root, selector)) if (!instances.has(node)) setup(node);
    }
    for (const scene of all(root, '[data-directory-passage]')) {
      if (scenes.has(scene)) continue;
      scenes.add(scene); resize?.observe(scene);
      const anchor = scene.querySelector('.passage-photo-anchor');
      if (anchor) resize?.observe(anchor);
    }
    measure();
    for (const canvas of doc.querySelectorAll('[data-art-canvas]')) canvas.setAttribute('data-art-ready', '');
    loadGate();
  }

  function unmount(root) {
    for (const [node, destroy] of instances) if (node === root || root.contains(node)) { destroy(); instances.delete(node); }
    for (const scene of scenes) if (scene === root || root.contains(scene)) {
      scenes.delete(scene); resize?.unobserve(scene);
      const anchor = scene.querySelector('.passage-photo-anchor');
      if (anchor) resize?.unobserve(anchor);
    }
  }

  function loadGate() {
    if (!scenes.size || gateRequest) return;
    const canvas = doc.querySelector('[data-art-canvas]');
    const source = canvas?.dataset.artGateUrl || new URL('./art-gate.js', import.meta.url).href;
    canvas?.setAttribute('data-art-gate-status', 'loading');
    gateRequest = Promise.resolve().then(() => loadGateModule ? loadGateModule(source) : import(source)).then(module => {
      if (disposed) return;
      gate = module;
      canvas?.setAttribute('data-art-gate-status', 'ready');
      schedule();
    }).catch(() => {
      if (!disposed) canvas?.setAttribute('data-art-gate-status', 'unavailable');
    });
  }

  const preferencesChanged = () => { measure(); journals.forEach(sync => sync()); };
  for (const query of Object.values(media)) on(query, 'change', preferencesChanged);
  on(win, 'scroll', schedule, { passive: true });
  on(win, 'resize', schedule);
  on(win, 'pageshow', () => { pageActive = true; schedule(); journals.forEach(sync => sync()); });
  on(win, 'pagehide', () => { pageActive = false; journals.forEach(sync => sync()); });
  on(win, 'beforeprint', () => { printing = true; preferencesChanged(); });
  on(win, 'afterprint', () => { printing = false; preferencesChanged(); });
  on(doc, 'visibilitychange', () => journals.forEach(sync => sync()));
  on(doc, 'click', event => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    const trigger = event.target.closest?.('[data-art-enter-gate]');
    if (!trigger) return;
    const passage = doc.querySelector('[data-art-paintings]');
    const stage = passage?.querySelector('.stage');
    if (!stage) return;
    event.preventDefault();
    if (homeStill()) passage.querySelector('.inner-room')?.scrollIntoView({ block: 'start', behavior: 'auto' });
    else {
      const offset = parseFloat(win.getComputedStyle(stage).top) || 0;
      win.scrollTo({ top: win.scrollY + passage.getBoundingClientRect().top - offset, behavior: 'auto' });
    }
    schedule();
  });
  on(doc, 'shopify:section:load', event => mount(event.target));
  on(doc, 'shopify:section:unload', event => { unmount(event.target); schedule(); });
  on(doc, 'shopify:section:reorder', schedule);
  mount(doc);
  doc.fonts?.ready.then(() => { if (!disposed) schedule(); });
  return () => {
    disposed = true; win.cancelAnimationFrame(frame); lifetime.abort(); unmount(doc); resize?.disconnect();
    for (const canvas of doc.querySelectorAll('[data-art-canvas], .canvas')) { canvas.classList.add('reduced'); canvas.removeAttribute('data-art-ready'); }
  };
}

if (typeof document !== 'undefined') {
  const boot = () => { window.__chiMollyArtCleanup?.(); window.__chiMollyArtCleanup = startArtSite(document); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
